import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MESSAGE_THREAD_CATEGORIES,
  type Message,
  type MessageThread,
  type MessageThreadCategory,
  type MessageThreadKind,
} from '@emr/shared';

import {
  getMyThreadDetail,
  listMyThreads,
  replyToMyThread,
  startMyThread,
} from '../../api/patientPortal/messaging.ts';
import { patientPortalErrorMessage } from '../../api/patientPortal/errors.ts';
import { AsyncSection } from '../../components/AsyncState.tsx';
import {
  Badge,
  Button,
  ErrorText,
  Field,
  Panel,
  SelectField,
  TextAreaField,
} from '../../components/primitives.tsx';
import { useHasRoomForInboxSplitView } from '../../hooks/useMediaQuery.ts';
import { useDateTimeFormat } from '../../format/dateTimeFormatContext.ts';
import { formatDateAndTime } from '../../format/formatDateTime.ts';
import { PortalHelpLink } from '../help/PortalHelpLink.tsx';
import { usePatientSession } from '../session/PatientSessionContext.ts';

const KIND_FILTERS = ['all', 'care_team', 'general'] as const;
type KindFilter = (typeof KIND_FILTERS)[number];

const STATUS_FILTERS = ['open', 'closed'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

/**
 * Secure messaging (`BACKLOG.md` W2 pillar 3, `docs/decisions/patient-portal-messaging-scope.md`).
 * Two-pane on desktop, one pane at a time on a phone — reuses `useHasRoomForInboxSplitView`
 * directly rather than deriving a second breakpoint: that hook's own header already measured this
 * exact layout shape (a thread list beside an open thread's messages and compose box) against
 * `AppShell`'s desktop nav rail, and this shell's own header is narrower, so the measured
 * breakpoint still holds with room to spare.
 *
 * A patient's own two thread kinds — `care_team` (requires an active care-team relationship;
 * offered only when one exists, per the decision doc's own "no active relationship, no thread to
 * send to") and `general` (always available, with a display-only category).
 */
export function PortalMessagesPage() {
  const { t } = useTranslation('portalMessages');
  const hasRoomForSplit = useHasRoomForInboxSplitView();

  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);

  const threadsQuery = useQuery({
    queryKey: ['portal', 'messaging', 'threads', kindFilter, statusFilter],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      listMyThreads(
        {
          ...(kindFilter === 'all' ? {} : { kind: kindFilter }),
          status: statusFilter,
        },
        signal,
      ),
  });

  const filtersPanel = (
    <Panel title={t('filters.panelTitle')}>
      <div className="flex flex-col gap-3">
        <SelectField
          label={t('filters.kindLabel')}
          value={kindFilter}
          onChange={(event) => {
            const { value } = event.target;
            if ((KIND_FILTERS as readonly string[]).includes(value)) {
              setKindFilter(value as KindFilter);
            }
          }}
        >
          {KIND_FILTERS.map((kind) => (
            <option key={kind} value={kind}>
              {t(`filters.kindOption.${kind}`)}
            </option>
          ))}
        </SelectField>
        <SelectField
          label={t('filters.statusLabel')}
          value={statusFilter}
          onChange={(event) => {
            const { value } = event.target;
            if ((STATUS_FILTERS as readonly string[]).includes(value)) {
              setStatusFilter(value as StatusFilter);
            }
          }}
        >
          {STATUS_FILTERS.map((status) => (
            <option key={status} value={status}>
              {t(`filters.statusOption.${status}`)}
            </option>
          ))}
        </SelectField>
        <Button
          onClick={() => {
            setComposing(true);
            setSelectedThreadId(null);
          }}
        >
          {t('filters.newMessage')}
        </Button>
      </div>
    </Panel>
  );

  const listSection = threadsQuery.isPending ? (
    <Panel>
      <p className="m-0 text-sm text-ink-muted">{t('list.loading')}</p>
    </Panel>
  ) : threadsQuery.isError ? (
    <ErrorText message={patientPortalErrorMessage(threadsQuery.error)} />
  ) : (
    <ThreadList
      threads={threadsQuery.data}
      selectedThreadId={hasRoomForSplit ? selectedThreadId : null}
      onSelect={(threadId) => {
        setSelectedThreadId(threadId);
        setComposing(false);
      }}
    />
  );

  const detailSection = composing ? (
    <ComposePanel
      onCreated={(thread) => {
        setComposing(false);
        setSelectedThreadId(thread.id);
        void threadsQuery.refetch();
      }}
      onCancel={() => {
        setComposing(false);
      }}
    />
  ) : selectedThreadId !== null ? (
    <ThreadDetailPanel
      threadId={selectedThreadId}
      onReplySent={() => {
        void threadsQuery.refetch();
      }}
      onBack={
        hasRoomForSplit
          ? undefined
          : () => {
              setSelectedThreadId(null);
            }
      }
    />
  ) : (
    <Panel>
      <p className="m-0 text-sm text-ink-muted">{t('detail.selectPrompt')}</p>
    </Panel>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <h1 className="m-0 text-xl font-semibold text-ink">{t('heading')}</h1>
        <PortalHelpLink topicId="messaging-your-care-team" />
      </div>

      {hasRoomForSplit ? (
        <div className="flex items-start gap-4">
          <div className="flex w-full max-w-sm shrink-0 flex-col gap-4">
            {filtersPanel}
            {listSection}
          </div>
          <div className="min-w-0 flex-1">{detailSection}</div>
        </div>
      ) : selectedThreadId !== null || composing ? (
        detailSection
      ) : (
        <div className="flex flex-col gap-4">
          {filtersPanel}
          {listSection}
        </div>
      )}
    </div>
  );
}

function ThreadList({
  threads,
  selectedThreadId,
  onSelect,
}: {
  readonly threads: readonly MessageThread[];
  readonly selectedThreadId: string | null;
  readonly onSelect: (threadId: string) => void;
}) {
  const { t } = useTranslation('portalMessages');
  const { preference } = useDateTimeFormat();

  if (threads.length === 0) {
    return (
      <Panel>
        <p className="m-0 text-sm text-ink-muted">{t('list.empty')}</p>
      </Panel>
    );
  }

  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {threads.map((thread) => (
        <li key={thread.id}>
          <button
            type="button"
            onClick={() => {
              onSelect(thread.id);
            }}
            aria-current={selectedThreadId === thread.id ? 'true' : undefined}
            className={[
              'w-full rounded-control border p-3 text-left',
              selectedThreadId === thread.id
                ? 'border-accent-text bg-raised'
                : 'border-line bg-surface hover:bg-raised',
            ].join(' ')}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium text-ink">
                {thread.subject ?? t(`filters.kindOption.${thread.kind}`)}
              </span>
              <Badge tone={thread.status === 'open' ? 'accent' : 'neutral'}>
                {t(`filters.statusOption.${thread.status}`)}
              </Badge>
            </div>
            <p className="m-0 mt-1 text-xs text-ink-muted">
              {t(`filters.kindOption.${thread.kind}`)}
              {thread.category !== null ? ` · ${t(`compose.category.${thread.category}`)}` : ''}
              {' · '}
              {formatDateAndTime(thread.updatedAt, preference)}
            </p>
          </button>
        </li>
      ))}
    </ul>
  );
}

function ThreadDetailPanel({
  threadId,
  onReplySent,
  onBack,
}: {
  readonly threadId: string;
  readonly onReplySent: () => void;
  readonly onBack: (() => void) | undefined;
}) {
  const { t } = useTranslation('portalMessages');
  const { preference } = useDateTimeFormat();
  const session = usePatientSession();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');

  const detailQuery = useQuery({
    queryKey: ['portal', 'messaging', 'thread', threadId],
    queryFn: ({ signal }: { signal: AbortSignal }) => getMyThreadDetail(threadId, signal),
  });

  const reply = useMutation({
    mutationFn: replyToMyThread,
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['portal', 'messaging', 'thread', threadId] });
      onReplySent();
    },
  });

  return (
    <Panel>
      <AsyncSection query={detailQuery} loadingLabel={t('detail.loading')}>
        {({ thread, messages }) => (
          <div className="flex flex-col gap-3">
            {onBack !== undefined ? (
              <Button variant="secondary" onClick={onBack} className="self-start">
                {t('detail.back')}
              </Button>
            ) : null}

            <div>
              <h2 className="m-0 text-base font-semibold text-ink">
                {thread.subject ?? t(`filters.kindOption.${thread.kind}`)}
              </h2>
              <p className="m-0 mt-1 text-xs text-ink-muted">
                {t(`filters.kindOption.${thread.kind}`)}
                {thread.category !== null ? ` · ${t(`compose.category.${thread.category}`)}` : ''}
              </p>
            </div>

            <ul className="m-0 flex list-none flex-col gap-2 p-0">
              {messages.map((message: Message) => (
                <li
                  key={message.id}
                  className={[
                    'max-w-[85%] rounded-control border border-line p-3 text-sm break-words [overflow-wrap:anywhere]',
                    message.senderRole === 'patient' ? 'ml-auto bg-accent-soft' : 'bg-surface',
                  ].join(' ')}
                >
                  <p className="m-0 whitespace-pre-wrap">{message.body}</p>
                  <p className="m-0 mt-1 text-xs text-ink-muted">
                    {message.senderRole === 'patient'
                      ? t('detail.fromMe', { name: session.firstName })
                      : t('detail.fromStaff')}{' '}
                    · {formatDateAndTime(message.createdAt, preference)}
                  </p>
                </li>
              ))}
            </ul>

            <form
              className="flex flex-col gap-2 border-t border-line pt-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (body.trim() === '') return;
                reply.mutate({ threadId: thread.id, body });
              }}
            >
              <TextAreaField
                label={t('detail.replyLabel')}
                rows={3}
                value={body}
                onChange={(event) => {
                  setBody(event.target.value);
                }}
              />
              {reply.isError ? (
                <ErrorText message={patientPortalErrorMessage(reply.error)} />
              ) : null}
              <Button type="submit" pending={reply.isPending} className="self-start">
                {reply.isPending ? t('detail.sending') : t('detail.send')}
              </Button>
            </form>
          </div>
        )}
      </AsyncSection>
    </Panel>
  );
}

function ComposePanel({
  onCreated,
  onCancel,
}: {
  readonly onCreated: (thread: MessageThread) => void;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation('portalMessages');
  const [kind, setKind] = useState<MessageThreadKind>('general');
  const [category, setCategory] = useState<MessageThreadCategory>('general_question');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const create = useMutation({
    mutationFn: startMyThread,
    onSuccess: (result) => {
      onCreated(result.thread);
    },
  });

  return (
    <Panel title={t('compose.panelTitle')}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate({
            kind,
            ...(kind === 'general' ? { category } : {}),
            ...(subject.trim() === '' ? {} : { subject }),
            body,
          });
        }}
      >
        <SelectField
          label={t('compose.kindLabel')}
          value={kind}
          onChange={(event) => {
            const { value } = event.target;
            if (value === 'care_team' || value === 'general') setKind(value);
          }}
        >
          <option value="general">{t('filters.kindOption.general')}</option>
          <option value="care_team">{t('filters.kindOption.care_team')}</option>
        </SelectField>

        {kind === 'general' ? (
          <SelectField
            label={t('compose.categoryLabel')}
            value={category}
            onChange={(event) => {
              const { value } = event.target;
              if ((MESSAGE_THREAD_CATEGORIES as readonly string[]).includes(value)) {
                setCategory(value as MessageThreadCategory);
              }
            }}
          >
            {MESSAGE_THREAD_CATEGORIES.map((option) => (
              <option key={option} value={option}>
                {t(`compose.category.${option}`)}
              </option>
            ))}
          </SelectField>
        ) : (
          <p className="m-0 text-sm text-ink-muted">{t('compose.careTeamHint')}</p>
        )}

        <Field
          label={t('compose.subjectLabel')}
          value={subject}
          onChange={(event) => {
            setSubject(event.target.value);
          }}
        />
        <TextAreaField
          label={t('compose.bodyLabel')}
          rows={4}
          required
          value={body}
          onChange={(event) => {
            setBody(event.target.value);
          }}
        />

        {create.isError ? <ErrorText message={patientPortalErrorMessage(create.error)} /> : null}

        <div className="flex gap-2">
          <Button type="submit" pending={create.isPending}>
            {create.isPending ? t('compose.sending') : t('compose.send')}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t('compose.cancel')}
          </Button>
        </div>
      </form>
    </Panel>
  );
}
